import React, { useState, useEffect } from 'react';
import { Modal, Alert, Typography, Tag, Space, Table, Spin, Tooltip } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Text } = Typography;

const STATUS_COLOR = {
    pending: 'blue',
    processing: 'orange',
    processed: 'green',
    failed: 'red',
    duplicate: 'purple',
};

const ResetOrderTempModal = ({ visible, onCancel, onSuccess, selectedOrderIds = [] }) => {
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [checkData, setCheckData] = useState([]);
    const [selectedTempOrderIds, setSelectedTempOrderIds] = useState([]);

    // Fetch existence data whenever modal opens
    useEffect(() => {
        if (visible && selectedOrderIds.length > 0) {
            setChecking(true);
            api.post('/orders/check-processing-temp', { orderIds: selectedOrderIds })
                .then(res => {
                    if (res.data?.success) {
                        const rows = res.data.data || [];
                        setCheckData(rows);
                        setSelectedTempOrderIds(
                            rows
                                .filter(record => record.exists && record.status !== 'processed')
                                .map(record => record.orderId)
                        );
                    }
                })
                .catch(err => console.error('Error checking processing temp:', err))
                .finally(() => setChecking(false));
        } else {
            setCheckData([]);
            setSelectedTempOrderIds([]);
        }
    }, [visible, selectedOrderIds]);

    const existsCount = checkData.filter(r => r.exists).length;
    const notExistsCount = checkData.filter(r => !r.exists).length;
    const processedCount = checkData.filter(record => record.exists && record.status === 'processed').length;

    const handleOk = async () => {
        if (selectedTempOrderIds.length === 0) return;

        setLoading(true);
        try {
            const response = await api.post('/orders/reset-processing-temp', {
                orderIds: selectedTempOrderIds
            });
            const data = response.data;
            if (data.success) {
                onSuccess && onSuccess(data);
            }
        } catch (error) {
            console.error('Error resetting order processing temp:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Order ID',
            dataIndex: 'orderId',
            key: 'orderId',
            render: (id) => <Text code style={{ fontSize: 12 }}>{id}</Text>,
        },
        {
            title: 'Trong temp table',
            dataIndex: 'exists',
            key: 'exists',
            width: 130,
            align: 'center',
            render: (exists) =>
                exists ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Đã có</Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="default">Chưa có</Tag>
                ),
        },
        {
            title: 'Status hiện tại',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'center',
            render: (status) =>
                status ? (
                    <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Retry',
            dataIndex: 'retryCount',
            key: 'retryCount',
            width: 60,
            align: 'center',
            render: (count, record) =>
                record.exists ? (
                    <Tag color={count > 0 ? 'orange' : 'default'}>{count ?? 0}</Tag>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Lỗi',
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            ellipsis: true,
            render: (msg) =>
                msg ? (
                    <Tooltip title={msg}>
                        <Tag icon={<WarningOutlined />} color="warning" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {msg}
                        </Tag>
                    </Tooltip>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    <ReloadOutlined />
                    Fix Order Processing Temp
                </Space>
            }
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Reset về Pending"
            cancelText="Hủy"
            confirmLoading={loading}
            okButtonProps={{ disabled: checking || selectedTempOrderIds.length === 0 }}
            width={780}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Alert
                    message="Xác nhận reset"
                    description={
                        <>
                            Cập nhật bảng <Text code>order_processing_temp</Text> cho các order đã chọn:{' '}
                            <Text code>status = 'pending'</Text>,{' '}
                            <Text code>retry_count = 0</Text>,{' '}
                            <Text code>error_message = null</Text>.{' '}
                            Các record sẽ được xử lý lại trong lần chạy job tiếp theo.
                        </>
                    }
                    type="warning"
                    showIcon
                />

                <Space>
                    <Text strong>Orders được chọn: {selectedOrderIds.length}</Text>
                    {!checking && checkData.length > 0 && (
                        <>
                            <Tag icon={<CheckCircleOutlined />} color="success">Đã có trong temp: {existsCount}</Tag>
                            {notExistsCount > 0 && (
                                <Tag icon={<CloseCircleOutlined />} color="default">Chưa có: {notExistsCount}</Tag>
                            )}
                            <Tag color="processing">Sẽ reset: {selectedTempOrderIds.length}</Tag>
                            {processedCount > 0 && (
                                <Tag color="success">Processed không chọn mặc định: {processedCount}</Tag>
                            )}
                        </>
                    )}
                </Space>

                <Spin spinning={checking} tip="Đang kiểm tra...">
                    <Table
                        size="small"
                        columns={columns}
                        dataSource={checkData.length > 0 ? checkData : selectedOrderIds.map(id => ({ orderId: id, exists: false }))}
                        rowKey="orderId"
                        rowSelection={{
                            selectedRowKeys: selectedTempOrderIds,
                            onChange: (keys) => setSelectedTempOrderIds(keys),
                            getCheckboxProps: (record) => ({
                                disabled: !record.exists
                            }),
                        }}
                        pagination={false}
                        scroll={{ y: 260 }}
                        rowClassName={(record) => record.exists ? '' : 'ant-table-row-disabled'}
                    />
                </Spin>
            </Space>
        </Modal>
    );
};

export default ResetOrderTempModal;
